import type { Handler, Match, RouteHooks } from "navigo";
import Navigo from "navigo";
import type { ChildDom } from "vanjs-core";
import type { Api } from "./api";
import type { ErrorSection } from "./van/error-section";
import type { LoadingScreen } from "./van/loading-screen";
import type { ViewComponent } from "./van/view-component";

export class Router {
	#jumpToClosestParent(path: string): void {
		if (path === "") {
			window.location.href = window.location.origin;
			return;
		}

		const parent = path.split("/").slice(0, -1).join("/");
		const match = this.#navigo.match(parent);
		if (!match) {
			this.#jumpToClosestParent(parent);
			return;
		}

		this.jump(parent);
	}

	down(target: string): void {
		target = window.location.pathname + target;
		this.jump(target);
	}

	jump(target: string): void {
		this.#navigo.navigate(target);
	}

	registerRoute(
		path: string,
		Route: RouteConstructor,
		hooks?: RouteHooks,
	): void {
		const routeHandler = (match: Match | undefined): void => {
			if (!this.routeContext) {
				throw "missing route context";
			}

			const route = new Route(this.routeContext);

			if (!match) {
				throw "prepare route: match is undefined";
			}

			const prepareRoute = new Promise((resolve) => {
				this.mountRoute(route.ChildDom);
				setTimeout(() => {
					route.refresh(match).then(resolve);
				}, 200);
			});

			if (this.routeContext) {
				this.routeContext.loadingScreen.waitFor(prepareRoute);
			}
		};

		this.#navigo.on(path, routeHandler, hooks);
	}

	registerRouteDEPRECATED(
		path: string,
		page: Handler,
		hooks?: RouteHooks,
	): void {
		const routeHandler: Handler = (match: Match | undefined) => {
			const prepareRoute = new Promise((resolve) => {
				page(match);
				resolve(true);
			});
			if (this.routeContext)
				this.routeContext.loadingScreen.waitFor(prepareRoute);
		};
		this.#navigo.on(path, routeHandler, hooks);
	}

	resolve(): void {
		this.#navigo.resolve();
	}

	up(): void {
		const hasHistory =
			window.history.length > 1 &&
			document.referrer.startsWith(window.location.origin);

		const isLocalhost =
			window.location.hostname === "localhost" ||
			window.location.hostname === "127.0.0.1";

		if (hasHistory || (isLocalhost && !document.referrer)) {
			window.history.back();
			return;
		}

		this.#jumpToClosestParent(window.location.pathname);
	}

	#navigo;
	mountRoute;
	routeContext: RouteContext | undefined;

	constructor(args: RouterParams) {
		this.#navigo = new Navigo(args.rootPath);
		this.#navigo.notFound(args.notFoundHandler);
		this.mountRoute = args.mountRoute;
	}
}

export interface Route extends ViewComponent {
	refresh: (match: import("navigo").Match) => Promise<void>;
}

export interface RouteContext {
	api: Api;
	error: ErrorSection;
	loadingScreen: LoadingScreen;
	router: Router;
	sp2gw: Api;
}

interface RouterParams {
	mountRoute: (childDom: ChildDom) => void;
	notFoundHandler: Handler;
	rootPath: string;
}

type RouteConstructor = new (ctx: RouteContext) => Route;
